
"use client";

import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,TargetAndTransition,

} from "framer-motion";
import {
  Layers,
  Banknote,
  CheckCircle2,
  ArrowRight,
  LucideIcon
} from "lucide-react";
import React, { useEffect, useState, useRef } from "react";

interface ComparisonCardProps {
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  icon: LucideIcon;
  target: string;
  isAdvanced?: boolean;
  index: number;
  waveIndex: number;
}

const ComparisonCard = ({
  title,
  subtitle,
  description,
  features,
  icon: Icon,
  target,
  isAdvanced,
  index,
  waveIndex
}: ComparisonCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
 

  // Base parallax
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);

  const rotateX = useSpring(
    useTransform(y, [0, 1], ["8deg", "-8deg"]),
    { stiffness: 80, damping: 20 }
  );

  

  // Apple keynote wave physics
  const isActive = waveIndex === index;

  const cinematicMotion: TargetAndTransition = isActive
  ? {
      y: -28,
      rotateY: 14,
      scale: 1.04,
      boxShadow: "0 60px 120px rgba(0,0,0,0.18)",
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 18
      }
    }
  : {
      y: 0,
      rotateY: 0,
      scale: 1,
      boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 22
      }
    };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width);
    y.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      animate={cinematicMotion}
      style={{
        rotateX,
        transformStyle: "preserve-3d"
      }}
      className="relative p-10 md:p-12 rounded-[2.5rem] border bg-white border-slate-200 transition-all duration-700"
    >
      <div style={{ transform: "translateZ(60px)" }}>
        <div className="flex items-center gap-5 mb-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-blue-50 text-blue-600 shadow-lg">
            <Icon size={26} strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">
              {title}
            </h3>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 font-bold">
              {subtitle}
            </p>
          </div>
        </div>

        <p className="text-slate-500 mb-10 text-lg leading-relaxed font-light">
          {description}
        </p>

        <ul className="space-y-4 mb-12">
          {features.map((item, i) => (
            <li key={i} className="flex items-center gap-3">
              <CheckCircle2 size={18} className="text-blue-500" />
              <span className="text-sm text-slate-600 font-light">
                {item}
              </span>
            </li>
          ))}
        </ul>

        <div className="pt-8 border-t border-slate-200 flex justify-between items-center">
          <div>
            <span className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
              Target Audience
            </span>
            <span className="text-xs font-bold text-slate-700">
              {target}
            </span>
          </div>

          {isAdvanced && (
            <motion.button
              whileHover={{ x: 6 }}
              className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-blue-600"
            >
              Protocol Details <ArrowRight size={16} />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const GaoComparison = () => {
  const [waveIndex, setWaveIndex] = useState(0);

  // Apple-style wave sequence
  useEffect(() => {
    let current = 0;

    const interval = setInterval(() => {
      setWaveIndex(current);
      current++;

      if (current > 1) current = 0; // loop smooth
    }, 2200); // slower, cinematic pacing

    return () => clearInterval(interval);
  }, []);

  const coreFeatures = [
    "Domain-based identity provisioning",
    "Encrypted communication (Mail & Chat)",
    "Structured knowledge repositories",
    "Document storage architecture",
    "Role-based access control (RBAC)",
    "System-level audit logging"
  ];
  
  const businessFeatures = [
    "Integrated payment processing hub",
    "Checkout & QR infrastructure",
    "Loyalty and incentive systems",
    "Affiliate management console",
    "Merchant analytics dashboard",
    "Multi-entity access control",
    "Regulatory compliance modules"
  ];

  return (
    <section
      className="relative py-32 bg-[#F8F9FF]"
      style={{ perspective: "2000px" }}
    >
      <div className="container mx-auto px-6">
      <div className="text-center max-w-4xl mx-auto mb-24">
            <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            className="inline-block rounded-full px-4 py-1.5 font-mono border border-indigo-100 bg-indigo-50 text-[10px] font-bold tracking-[0.3em] uppercase mb-4 text-blue-600"
          >
             Strategic Architecture
          </motion.div>
          <motion.h2 
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  className="text-5xl md:text-7xl tracking-tighter mb-8 leading-tight"
>
  Identity & Coordination vs Revenue Infrastructure
</motion.h2>

<p className="text-neutral-500 text-lg md:text-xl font-light italic max-w-2xl mx-auto leading-relaxed">
  Core Workspace establishes governance and coordination.  
  <br className="hidden md:block"/>  
  Business OS extends the domain into commercial scale.
</p>
          {/* <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl tracking-tighter mb-8 leading-tight"
          >
          Core vs Advanced Business OS     
          </motion.h2>
          <p className="text-neutral-500 text-lg md:text-xl font-light italic max-w-2xl mx-auto leading-relaxed">
            "Core Workspace builds the community. <br className="hidden md:block"/> Business OS scales the economy."
          </p> */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <ComparisonCard
            index={0}
            waveIndex={waveIndex}
            title="Core Workspace"
            subtitle="Identity & Coordination Layer"
            description="Establishes the operational foundation of an organization through structured identity governance and internal coordination systems."
            features={coreFeatures}
            icon={Layers}
            target="All Users & Teams"
          />

          <ComparisonCard
            index={1}
            waveIndex={waveIndex}
            title="Business OS"
            subtitle="Revenue & Commercial Layer"
            description="Extends the domain into a revenue-generating infrastructure built for merchants, operators, and high-scale commercial entities."
            features={businessFeatures}
            icon={Banknote}
            target="Merchants & High-scale Entities"
            isAdvanced
          />
        </div>
      </div>
    </section>
  );
};

export default GaoComparison;